/**
 * Cygnus Systems — wireless FIRE detection catalog (UK manufacturer).
 *
 * ⚠ NOTE on brand identity: Cygnus Systems (cygnus-systems.com) is a
 * fire-alarm manufacturer, NOT an intruder-alarm manufacturer. Their
 * lineup is EN 54-certified wireless fire systems (SN-Pro / SN-100,
 * formerly SmartNet) used on construction sites and industrial
 * properties. The "Cygnus N² / 850-Lite" intruder-panel models that
 * sometimes get attributed to them do not exist on the official site
 * — those are likely confused with Pyronix / Texecom / Risco.
 *
 * Schema mapping for fire products:
 *   - Panels → `alarm-panel` with `grade: 2` (placeholder; the real
 *     standard is EN 54-2 not EN 50131) and `partitions: 1`. Tagline
 *     identifies them as fire panels.
 *   - Smoke detector → `smoke-detector` with `standard: ['EN 54-7']`.
 *   - Sounder bases → `indoor-sounder` with `acoustic: 'dynamic'`.
 *   - Manual call point + radio bases + I/O interfaces have no
 *     matching schema category — omitted for now (see report).
 *
 * Verified against cygnus-systems.com product pages on 2026-05-15.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const CYGNUS_FIRE_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Fire control panels (SN-Pro 96/64 + SN-100 32)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'cygnus-sn-pro-96',
    manufacturer: 'Cygnus',
    model: 'SN-Pro Control Panel (SN.CIE20.A) — 96 zone',
    category: 'alarm-panel',
    tagline: 'EN 54-2/4/25 wireless FIRE panel, 96 zones, 511-device 868 MHz mesh, networkable to 5 panels',
    verified: true,
    datasheetUrl: 'https://www.cygnus-systems.com/en-gb/products/cygnus-sn/cygnus-sn-pro/control-panel-96-zones/',
    specs: {
      series: 'Cygnus SN-Pro',
      zonesOnboard: 96,
      zonesMax: 96,
      partitions: 1,
      grade: 2,
      communicators: ['wireless'],
    },
  },

  {
    id: 'cygnus-sn-pro-64',
    manufacturer: 'Cygnus',
    model: 'SN-Pro Control Panel (SN.CIE20.A) — 64 zone',
    category: 'alarm-panel',
    tagline: 'EN 54-2/4/25 wireless FIRE panel, 64 zones (upgradable to 96), 868 MHz mesh',
    verified: true,
    datasheetUrl: 'https://www.cygnus-systems.com/en-gb/products/cygnus-sn/cygnus-sn-pro/control-panel-64-zones/',
    specs: {
      series: 'Cygnus SN-Pro',
      zonesOnboard: 64,
      zonesMax: 96,
      partitions: 1,
      grade: 2,
      communicators: ['wireless'],
    },
  },

  {
    id: 'cygnus-sn-100',
    manufacturer: 'Cygnus',
    model: 'SN-100 Control Panel (S1.CIE10.A) — 32 zone',
    category: 'alarm-panel',
    tagline: 'EN 54-2/4/25 entry-level wireless FIRE panel, 32 zones, 100-device 868 MHz',
    verified: true,
    datasheetUrl: 'https://www.cygnus-systems.com/en-gb/products/cygnus-sn/cygnus-sn-100/sn-100-control-panel-32-zone-868-mhz-with-zone-leds/',
    specs: {
      series: 'Cygnus SN-100',
      zonesOnboard: 32,
      zonesMax: 32,
      partitions: 1,
      grade: 2,
      communicators: ['wireless'],
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Wireless smoke detector
  // ─────────────────────────────────────────────────────────────

  {
    id: 'cygnus-sn-pro-optical-smoke',
    manufacturer: 'Cygnus',
    model: 'SN-Pro Optical Smoke Detector (SN.DTS0.1)',
    category: 'smoke-detector',
    tagline: 'EN 54-7 wireless optical smoke detector for SN-Pro, 3-year battery, 868 MHz mesh',
    verified: true,
    datasheetUrl: 'https://www.cygnus-systems.com/en-gb/products/cygnus-sn/cygnus-sn-pro/optical-smoke-detector/',
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 54-7'],
      standalone: false,
      batteryYears: 3,
      wirelessSystem: 'Cygnus SN 868 MHz mesh',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Wireless sounder bases (mapped to indoor-sounder)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'cygnus-sn-pro-sounder-base',
    manufacturer: 'Cygnus',
    model: 'SN-Pro Sounder Radio Base (SN.RB10.1)',
    category: 'indoor-sounder',
    tagline: 'EN 54-3 wireless ceiling fire sounder base, 32 tones, 868 MHz mesh, 3-year battery',
    verified: true,
    datasheetUrl: 'https://www.cygnus-systems.com/en-gb/products/cygnus-sn/cygnus-sn-pro/sounder-radio-base-white/',
    specs: {
      acoustic: 'dynamic',
      optical: 'none',
      batteryBackup: 'built-in',
    },
  },

  {
    id: 'cygnus-sn-pro-svi-base',
    manufacturer: 'Cygnus',
    model: 'SN-Pro SVI Sounder + Visual Indicator Base (SN.RB20.1A)',
    category: 'indoor-sounder',
    tagline: 'EN 54-3 wireless ceiling fire sounder + visual indicator base, 32 tones, 868 MHz',
    verified: true,
    datasheetUrl: 'https://www.cygnus-systems.com/en-gb/products/cygnus-sn/cygnus-sn-pro/sounder-vid-radio-base-white/',
    specs: {
      acoustic: 'dynamic',
      optical: 'led',
      batteryBackup: 'built-in',
    },
  },
];
